// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//
import { XProductFilters } from "@guestsupply/XDL/types";
import { xScreen } from "@guestsupply/XDL/Utilities";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getProductsDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import clearAllProductFilters from "@insite/client-framework/Store/Pages/ProductList/Handlers/ClearAllProductFilters";
import setView from "@insite/client-framework/Store/Pages/ProductList/Handlers/SetView";
import {
    getProductListDataView,
    getProductListDataViewProperty,
} from "@insite/client-framework/Store/Pages/ProductList/ProductListSelectors";
import translate from "@insite/client-framework/Translate";
import { AttributeTypeFacetModel, CategoryFacetModel, PriceFacetModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ProductListColumnsDrawer from "@insite/content-library/Widgets/ProductList/ProductListColumnsDrawer";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Drawer, { DrawerPresentationProps } from "@insite/mobius/Drawer";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Filter from "@insite/mobius/Icons/Filter";
import Tag from "@insite/mobius/Icons/Tag";
import Link, { LinkProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapStateToProps = (state: ApplicationState) => {
    const {
        pages: {
            productList: { productFilters, unfilteredApiParameter, filteredApiParameter },
        },
        context: {
            session: { currency, language },
        },
    } = state;
    if (getProductListDataView(state).value) {
        const unfilteredDataView = getProductsDataView(state, unfilteredApiParameter);
        const unfilteredProductCollection = unfilteredDataView.value ? unfilteredDataView : undefined;

        const filteredDataView = getProductsDataView(state, filteredApiParameter);
        const filteredProductCollection = filteredDataView.value ? filteredDataView : undefined;
        const mergeAttributeTypeFacets = (
            unfilteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
            filteredAttributeTypeFacets?: AttributeTypeFacetModel[] | null,
        ) => {
            if (!unfilteredAttributeTypeFacets) {
                return filteredAttributeTypeFacets;
            }

            if (!filteredAttributeTypeFacets) {
                return unfilteredAttributeTypeFacets;
            }

            return unfilteredAttributeTypeFacets.concat(
                filteredAttributeTypeFacets.filter(
                    o => !unfilteredAttributeTypeFacets.some(p => p.attributeTypeId === o.attributeTypeId),
                ),
            );
        };

        return {
            loaded: !!getProductListDataView(state).value,
            view: state.pages.productList.view,
            stockedItemsOnly: productFilters.stockedItemsOnly ?? false,
            previouslyPurchasedProducts: productFilters.previouslyPurchasedProducts ?? false,
            brandFacets: unfilteredProductCollection?.brandFacets || filteredProductCollection?.brandFacets,
            productLineFacets:
                unfilteredProductCollection?.productLineFacets || filteredProductCollection?.productLineFacets,
            categoryFacets: unfilteredProductCollection?.categoryFacets,
            priceFacets:
                unfilteredProductCollection?.priceRange?.priceFacets ||
                filteredProductCollection?.priceRange?.priceFacets,
            attributeTypeFacets: mergeAttributeTypeFacets(
                unfilteredProductCollection?.attributeTypeFacets,
                filteredProductCollection?.attributeTypeFacets,
            ),
            searchWithinQueries: productFilters.searchWithinQueries,
            selectedBrandIds: productFilters.brandIds,
            selectedProductLineIds: productFilters.productLineIds,
            selectedPriceFilters: productFilters.priceFilters,
            selectedAttributeValuesIds: productFilters.attributeValueIds,
            selectedCategoryId: productFilters.categoryId,
            currency,
            language,
            brandStandards: (state.pages.productList.productFilters as XProductFilters).brandStandards as any,
            isAvendraPricing: (state.pages.productList.productFilters as XProductFilters)?.isAvendraPricing as any,
        };
    }
    return {
        loaded: !!getProductListDataView(state).value,
        view: state.pages.productList.view,
        attributeTypeFacets: getProductListDataViewProperty(state, "attributeTypeFacets"),
        brandFacets: getProductListDataViewProperty(state, "brandFacets"),
        productLineFacets: getProductListDataViewProperty(state, "productLineFacets"),
        stockedItemsOnly: false,
        previouslyPurchasedProducts: false,
        categoryFacets: [] as CategoryFacetModel[],
        priceFacets: [] as PriceFacetModel[],
        searchWithinQueries: [] as string[],
        selectedBrandIds: [] as string[],
        selectedProductLineIds: [] as string[],
        selectedPriceFilters: [] as string[],
        selectedAttributeValuesIds: [] as string[],
        selectedCategoryId: "",
        currency: null,
        language: null,
        brandStandards: (state.pages.productList.productFilters as XProductFilters).brandStandards as any,
        isAvendraPricing: (state.pages.productList.productFilters as XProductFilters)?.isAvendraPricing as any,
    };
};

const mapDispatchToProps = {
    setView,
    clearAllProductFilters,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface ProductListTableViewFilterStyles {
    wrapper?: InjectableCss;
    filterClickable?: ClickablePresentationProps;
    filterIcon?: IconPresentationProps;
    tagClickable?: ClickablePresentationProps;
    tagIcon?: IconPresentationProps;
    filterDrawer?: DrawerPresentationProps;
    filterDrawerContentWrapper?: InjectableCss;
    // XNG-CHANGES
    filterLink?: LinkProps;
    clearAllFiltersButton?: ButtonPresentationProps;
    clearAllFiltersOuterWrapper?: InjectableCss;
    clearAllFiltersInnerWrapper?: InjectableCss;
}

export const viewSelectStyles: ProductListTableViewFilterStyles = {};

const styles = viewSelectStyles;

const ProductListTableViewFilter = ({
    id,
    loaded,
    view,
    attributeTypeFacets,
    brandFacets,
    productLineFacets,
    stockedItemsOnly,
    previouslyPurchasedProducts,
    priceFacets,
    searchWithinQueries,
    selectedBrandIds,
    selectedProductLineIds,
    selectedPriceFilters,
    selectedAttributeValuesIds,
    selectedCategoryId,
    clearAllProductFilters,
    brandStandards,
    isAvendraPricing,
}: Props) => {
    const [filterDrawerIsOpen, setFilterDrawerIsOpen] = useState(false);
    const [attributesDrawerIsOpen, setAttributesDrawerIsOpen] = useState(false);

    if (!loaded && view !== "Table") {
        return null;
    }

    const anyActiveFilters =
        searchWithinQueries?.length ||
        stockedItemsOnly ||
        previouslyPurchasedProducts ||
        (brandFacets && selectedBrandIds?.length) ||
        (productLineFacets && selectedProductLineIds?.length) ||
        (priceFacets && selectedPriceFilters?.length) ||
        (attributeTypeFacets && selectedAttributeValuesIds?.length) ||
        selectedCategoryId ||
        isAvendraPricing ||
        brandStandards?.length;

    const clickFilterIconHandler = () => {
        setFilterDrawerIsOpen(!filterDrawerIsOpen);
    };

    const clickTagIconHandler = () => {
        setAttributesDrawerIsOpen(!attributesDrawerIsOpen);
    };

    const filterDrawerCloseHandler = () => {
        setFilterDrawerIsOpen(false);
    };

    const attributesDrawerCloseHandler = () => {
        setAttributesDrawerIsOpen(false);
    };
    // XNG-CHANGES : replace clickable by link and remove tagIcon
    return (
        <>
            <StyledWrapper {...styles.wrapper}>
                <Link
                    aria-label="open filter drawer"
                    data-test-selector="productListFilterButton"
                    {...styles.filterLink}
                    onClick={clickFilterIconHandler}
                >
                    {translate("Refine by")}
                </Link>
                {/* {(attributeTypeFacets?.length || brandFacets?.length || productLineFacets?.length) && (
                    <Clickable
                        {...styles.tagClickable}
                        onClick={clickTagIconHandler}
                        data-test-selector="productListChooseColumns"
                    >
                        <Icon {...styles.tagIcon} />
                    </Clickable>
                )} */}
            </StyledWrapper>
            <Drawer
                {...styles.filterDrawer}
                isOpen={filterDrawerIsOpen}
                handleClose={filterDrawerCloseHandler}
                data-test-selector="productListFilterDrawer"
                headline={translate("Refine by")}
            >
                <StyledWrapper {...styles.filterDrawerContentWrapper}>
                    <Zone zoneName="Container" contentId={id} />
                </StyledWrapper>
                {anyActiveFilters && (
                    <StyledWrapper {...styles.clearAllFiltersOuterWrapper}>
                        <StyledWrapper {...styles.clearAllFiltersInnerWrapper}>
                            <Button
                                {...styles.clearAllFiltersButton}
                                onClick={() => clearAllProductFilters()}
                                aria-label={translate("Clear All Filters")}
                            >
                                {translate("Clear All Filters")}
                            </Button>
                        </StyledWrapper>
                    </StyledWrapper>
                )}
            </Drawer>
            <ProductListColumnsDrawer
                drawerIsOpen={attributesDrawerIsOpen}
                onDrawerClose={attributesDrawerCloseHandler}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ProductListTableViewFilter),
    definition: {
        group: "Product List",
        displayName: "Table View Filter",
        allowedContexts: ["ProductListPage"],
    },
};

export default widgetModule;
